import { useState, useEffect, useRef, useCallback } from 'react';
import { apiService } from '../services/api';
import { bookingService } from '../services/bookingService';
import { vehicleService } from '../services/vehicleService';

interface UseRealTimeDataOptions<T> {
  fetchFn: () => Promise<T>;
  interval?: number;
  enabled?: boolean;
  onSuccess?: (data: T) => void;
  onError?: (error: Error) => void;
}

export interface RealTimeDataResult<T> {
  data: T | null;
  loading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
  lastUpdated: Date | null;
  isStale: boolean;
}

interface BookingData {
  _id?: string;
  status: string;
  paymentStatus: string;
  totalAmount: number;
  createdAt: string | Date;
  customer?: {
    _id?: string;
    firstName?: string;
    lastName?: string;
    email?: string;
    phone?: string;
  };
  car?: {
    _id?: string;
    make?: string;
    model?: string;
    year?: number;
  };
  [key: string]: any;
}

interface FleetSummaryData {
  totalVehicles: number;
  availableVehicles: number;
  rentedVehicles: number;
  maintenanceVehicles: number;
  outOfServiceVehicles: number;
}

interface BookingSummaryData {
  totalBookings: number;
  pendingBookings: number;
  confirmedBookings: number;
  inProgressBookings: number;
  completedBookings: number;
  cancelledBookings: number;
  totalRevenue: number;
  pendingPayments: number;
  conflictedBookings: number;
  bookingsRequiringApproval: number;
}

interface DashboardMetricsData {
  totalBookings: number;
  activeBookings: number;
  todayBookings: number;
  weekBookings: number;
  monthBookings: number;
  lastWeekBookings: number;
  lastMonthBookings: number;
  totalRevenue: number;
  weekRevenue: number;
  monthRevenue: number;
  lastWeekRevenue: number;
  lastMonthRevenue: number;
  fleetUtilization: number;
  lastWeekUtilization: number;
  totalVehicles: number;
  availableVehicles: number;
  rentedVehicles: number;
  maintenanceVehicles: number;
  outOfServiceVehicles: number;
  pendingPayments: number;
  uniqueCustomers: number;
  newCustomersThisWeek: number;
  recentBookings: BookingData[];
  revenueChange: number;
  bookingsChange: number;
  utilizationChange: number;
  customersChange: number;
  revenueTrend: { date: string; revenue: number; bookings: number }[];
}

export const useRealTimeData = <T>({
  fetchFn,
  interval = 30000,
  enabled = true,
  onSuccess,
  onError,
}: UseRealTimeDataOptions<T>): RealTimeDataResult<T> => {
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);
  const [isStale, setIsStale] = useState(false);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const isMountedRef = useRef(true);

  const fetchData = useCallback(
    async (isInitial = false) => {
      if (!isMountedRef.current) return;

      try {
        if (isInitial) {
          setLoading(true);
        }
        setError(null);
        setIsStale(false);

        const result = await fetchFn();

        if (isMountedRef.current) {
          setData(result);
          setLastUpdated(new Date());
          onSuccess?.(result);
        }
      } catch (err) {
        if (isMountedRef.current) {
          const errorMessage =
            err instanceof Error ? err.message : 'An error occurred';
          setError(errorMessage);
          setIsStale(true);
          onError?.(err instanceof Error ? err : new Error(errorMessage));
          console.error('Real-time data fetch error:', err);
        }
      } finally {
        if (isMountedRef.current) {
          setLoading(false);
        }
      }
    },
    [fetchFn, onSuccess, onError],
  );

  const refresh = useCallback(async () => {
    await fetchData(false);
  }, [fetchData]);

  useEffect(() => {
    isMountedRef.current = true;

    if (!enabled) {
      setLoading(false);
      return;
    }

    fetchData(true);

    intervalRef.current = setInterval(() => {
      fetchData(false);
    }, interval);

    return () => {
      isMountedRef.current = false;
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [enabled, interval, fetchData]);

  useEffect(() => {
    if (!lastUpdated || !enabled) return;

    const staleTimeout = setTimeout(() => {
      if (isMountedRef.current) {
        setIsStale(true);
      }
    }, interval * 2);

    return () => clearTimeout(staleTimeout);
  }, [lastUpdated, interval, enabled]);

  return { data, loading, error, refresh, lastUpdated, isStale };
};

export const useDashboardMetrics = (refreshInterval = 30000) => {
  const [metrics, setMetrics] = useState<DashboardMetricsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);

  const fetchMetrics = useCallback(async () => {
    try {
      setError(null);

      const now = new Date();
      const rangeStart = new Date(now);
      rangeStart.setDate(now.getDate() - 45);

      const [bookingsRes, bookingSummaryRes, fleetRes] = await Promise.all([
        bookingService.getAllBookings({ startDate: rangeStart, endDate: now }),
        bookingService.getBookingSummary(),
        vehicleService.getFleetSummary(),
      ]);

      const bookings: BookingData[] =
        bookingsRes.success && bookingsRes.data
          ? (bookingsRes.data as unknown as BookingData[])
          : [];

      const bookingSummary: BookingSummaryData | null =
        bookingSummaryRes.success && bookingSummaryRes.data
          ? (bookingSummaryRes.data as unknown as BookingSummaryData)
          : null;

      const fleetSummary: FleetSummaryData =
        fleetRes.success && fleetRes.data
          ? (fleetRes.data as unknown as FleetSummaryData)
          : {
              totalVehicles: 0,
              availableVehicles: 0,
              rentedVehicles: 0,
              maintenanceVehicles: 0,
              outOfServiceVehicles: 0,
            };

      const todayStart = new Date(
        now.getFullYear(),
        now.getMonth(),
        now.getDate(),
      );
      const weekStart = new Date(todayStart);
      weekStart.setDate(weekStart.getDate() - 7);
      const lastWeekStart = new Date(weekStart);
      lastWeekStart.setDate(lastWeekStart.getDate() - 7);
      const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);
      const lastMonthStart = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      const lastMonthEnd = new Date(now.getFullYear(), now.getMonth(), 0);

      const activeBookingsFallback = bookings.filter((b) =>
        ['confirmed', 'in-progress', 'pending'].includes(b.status),
      );
      const todayBookings = bookings.filter(
        (b) => new Date(b.createdAt) >= todayStart,
      );
      const weekBookings = bookings.filter((b) => new Date(b.createdAt) >= weekStart);
      const lastWeekBookings = bookings.filter((b) => {
        const date = new Date(b.createdAt);
        return date >= lastWeekStart && date < weekStart;
      });
      const monthBookings = bookings.filter((b) => new Date(b.createdAt) >= monthStart);
      const lastMonthBookings = bookings.filter((b) => {
        const date = new Date(b.createdAt);
        return date >= lastMonthStart && date <= lastMonthEnd;
      });

      const weekRevenue = weekBookings.reduce(
        (sum, b) => sum + (b.totalAmount || 0),
        0,
      );
      const lastWeekRevenue = lastWeekBookings.reduce(
        (sum, b) => sum + (b.totalAmount || 0),
        0,
      );
      const monthRevenue = monthBookings.reduce(
        (sum, b) => sum + (b.totalAmount || 0),
        0,
      );
      const lastMonthRevenue = lastMonthBookings.reduce(
        (sum, b) => sum + (b.totalAmount || 0),
        0,
      );

      const customerEmails = new Set(
        bookings.map((b) => b.customer?.email).filter(Boolean),
      );
      const uniqueCustomers = customerEmails.size;

      const weekCustomerEmails = new Set(
        weekBookings.map((b) => b.customer?.email).filter(Boolean),
      );
      const lastWeekCustomerEmails = new Set(
        lastWeekBookings.map((b) => b.customer?.email).filter(Boolean),
      );
      const newCustomersThisWeek = weekCustomerEmails.size;

      const totalVehicles = fleetSummary.totalVehicles || 0;
      const rentedVehicles = fleetSummary.rentedVehicles || 0;
      const fleetUtilization =
        totalVehicles > 0 ? Math.round((rentedVehicles / totalVehicles) * 100) : 0;

      const revenueChange =
        lastWeekRevenue > 0
          ? Math.round(((weekRevenue - lastWeekRevenue) / lastWeekRevenue) * 100)
          : weekRevenue > 0
            ? 100
            : 0;

      const bookingsChange =
        lastWeekBookings.length > 0
          ? Math.round(
              ((weekBookings.length - lastWeekBookings.length) /
                lastWeekBookings.length) *
                100,
            )
          : weekBookings.length > 0
            ? 100
            : 0;

      const customersChange =
        lastWeekCustomerEmails.size > 0
          ? Math.round(
              ((weekCustomerEmails.size - lastWeekCustomerEmails.size) /
                lastWeekCustomerEmails.size) *
                100,
            )
          : weekCustomerEmails.size > 0
            ? 100
            : 0;

      const revenueTrend: { date: string; revenue: number; bookings: number }[] = [];
      for (let i = 6; i >= 0; i--) {
        const dayStart = new Date(todayStart);
        dayStart.setDate(dayStart.getDate() - i);
        const dayEnd = new Date(dayStart);
        dayEnd.setDate(dayEnd.getDate() + 1);

        const dayBookings = bookings.filter((b) => {
          const date = new Date(b.createdAt);
          return date >= dayStart && date < dayEnd;
        });

        revenueTrend.push({
          date: dayStart.toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
          }),
          revenue: dayBookings.reduce((sum, b) => sum + (b.totalAmount || 0), 0),
          bookings: dayBookings.length,
        });
      }

      const recentBookings = [...bookings]
        .sort(
          (a, b) =>
            new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
        )
        .slice(0, 10);

      setMetrics({
        totalBookings: bookingSummary?.totalBookings ?? bookings.length,
        activeBookings:
          bookingSummary?.pendingBookings !== undefined
            ? (bookingSummary.pendingBookings || 0) +
              (bookingSummary.confirmedBookings || 0) +
              (bookingSummary.inProgressBookings || 0)
            : activeBookingsFallback.length,
        todayBookings: todayBookings.length,
        weekBookings: weekBookings.length,
        monthBookings: monthBookings.length,
        lastWeekBookings: lastWeekBookings.length,
        lastMonthBookings: lastMonthBookings.length,
        totalRevenue:
          bookingSummary?.totalRevenue ??
          bookings.reduce((sum, b) => sum + (b.totalAmount || 0), 0),
        weekRevenue,
        monthRevenue,
        lastWeekRevenue,
        lastMonthRevenue,
        fleetUtilization,
        lastWeekUtilization: fleetUtilization,
        totalVehicles,
        availableVehicles: fleetSummary.availableVehicles || 0,
        rentedVehicles,
        maintenanceVehicles: fleetSummary.maintenanceVehicles || 0,
        outOfServiceVehicles: fleetSummary.outOfServiceVehicles || 0,
        pendingPayments:
          bookingSummary?.pendingPayments ??
          bookings.filter((b) => b.paymentStatus === 'pending').length,
        uniqueCustomers,
        newCustomersThisWeek,
        recentBookings,
        revenueChange,
        bookingsChange,
        utilizationChange: 0,
        customersChange,
        revenueTrend,
      });

      setLastUpdated(new Date());
    } catch (err) {
      console.error('Error fetching dashboard metrics:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch metrics');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchMetrics();
    const interval = setInterval(fetchMetrics, refreshInterval);
    return () => clearInterval(interval);
  }, [fetchMetrics, refreshInterval]);

  return { metrics, loading, error, lastUpdated, refresh: fetchMetrics };
};

export const useRealtimeBookings = (limit = 10, refreshInterval = 30000) => {
  const fetchBookings = useCallback(async (): Promise<BookingData[]> => {
    const now = new Date();
    const startDate = new Date(now);
    startDate.setDate(now.getDate() - 30);

    const response = await bookingService.getBookingsPage({
      startDate,
      endDate: now,
      page: 1,
      limit,
      lightweight: true,
    });
    const bookings: BookingData[] =
      response.success && response.data
        ? (response.data.items as unknown as BookingData[])
        : [];

    return bookings
      .sort(
        (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
      )
      .slice(0, limit);
  }, [limit]);

  return useRealTimeData<BookingData[]>({
    fetchFn: fetchBookings,
    interval: refreshInterval,
  });
};

export const useRealtimeFleet = (refreshInterval = 60000) => {
  const fetchFleet = useCallback(async (): Promise<FleetSummaryData> => {
    const response = await apiService.get<FleetSummaryData>('/api/cars/admin/fleet-summary');
    return (
      response.data || {
        totalVehicles: 0,
        availableVehicles: 0,
        rentedVehicles: 0,
        maintenanceVehicles: 0,
        outOfServiceVehicles: 0,
      }
    );
  }, []);

  return useRealTimeData<FleetSummaryData>({
    fetchFn: fetchFleet,
    interval: refreshInterval,
  });
};

export const useRealtimeAnalytics = (refreshInterval = 60000) => {
  const fetchAnalytics = useCallback(async () => {
    const response = await apiService.get('/api/website-analytics/summary');
    return response.data;
  }, []);

  return useRealTimeData({
    fetchFn: fetchAnalytics,
    interval: refreshInterval,
  });
};

export default useRealTimeData;
